import React from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  alpha,
  Stack,
  Card,
  CardContent,
  useMediaQuery,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import {
  EMAIL_API_KEY,
  FIREBASE_BASE_URL,
} from "../feature-configurator/utils/constants";

type PracticeGroupApi = {
  kinshipId: string;
  code: string;
  name: string;
  urlCode: string;
  multiClinicianBooking?: boolean;
  vetstoriaApiVersion?: string;
  externalName?: string;
  quickBookEnabled?: boolean;
  crmName?: string;
  ssoEnabled?: boolean;
  active?: boolean;
  partner?: string | null;
  practiceGroupId?: string | null;
};

type PracticeApi = {
  kinshipId: string;
  code: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  vetstoriaClinicHash?: string | null;
  vetstoriaLocationId?: string | null;
  hours?: string | null;
  oohService?: string | null;
  oohPhoneNumbers?: Array<{
    label?: string | null;
    phoneNumber?: string | null;
  }>;
  autoReplyEnabled?: boolean | null;
  autoReplyMessage?: string | null;
  ssoEnabled?: boolean | null;
  active?: boolean | null;
  practiceGroupKinshipId?: string | null;
  externalName?: string | null;
};

type PracticeRow = {
  id: string;
  url_code: string;
  partner: string;
  practice_group_name: string;

  practice_location_name: string;
  practice_location_address: string;
  practice_location_phone: string;
  practice_location_email: string;
  practice_location_opening_hours: string;

  ooh_service: string;
  ooh_phone: string;
  ooh_phone_numbers: Array<{
    label?: string | null;
    phoneNumber?: string | null;
  }>;

  vetstoria_clinic_hash: string;
  vetstoria_location_id: string;

  multi_clinician_booking: boolean;
  vetstoria_api_version: string;
  external_name: string;
  quick_book_enabled: boolean;
  crm_name: string;
  sso_enabled: boolean;
  practice_group_id: string;
  active: boolean;
  auto_reply_enabled: boolean;
  auto_reply_message: string;

  practice_kinship_id: string;
  practice_group_kinship_id: string;
};

function safeStr(v: unknown) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}
function safeBool(v: unknown) {
  return v === true;
}

function formatOohPhones(
  arr:
    | Array<{ label?: string | null; phoneNumber?: string | null }>
    | undefined,
) {
  if (!arr?.length) return "";
  return arr
    .map(p => {
      const label = (p.label || "").trim();
      const phone = (p.phoneNumber || "").trim();
      if (label && phone) return `${label}: ${phone}`;
      return phone || label;
    })
    .filter(Boolean)
    .join(" • ");
}

function formatOohPhonesMultiline(
  arr:
    | Array<{ label?: string | null; phoneNumber?: string | null }>
    | undefined,
) {
  if (!arr?.length) return "—";
  return arr
    .map(p => {
      const label = (p.label || "").trim();
      const phone = (p.phoneNumber || "").trim();
      if (label && phone) return `${label}: ${phone}`;
      return phone || label;
    })
    .filter(Boolean)
    .join("\n");
}

export default function PracticeConfigToolPage() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)"); // treat md- as "mobile/tablet"
  const [error, setError] = React.useState<string>("");
  const [permissionDenied, setPermissionDenied] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState<PracticeRow[]>([]);
  const [query, setQuery] = React.useState("");
  const requestConfig = React.useMemo(
    () => ({
      headers: {
        emailApiKey: EMAIL_API_KEY,
        "Content-Type": "application/json",
      },
    }),
    [],
  );
  const fetchAndMerge = React.useCallback(async () => {
    setLoading(true);
    setError("");
    setPermissionDenied(false);
    try {
      const [groupsRes, practicesRes] = await Promise.all([
        axiosInstance.get<PracticeGroupApi[]>(
          `${FIREBASE_BASE_URL}/vetinsight/practice_groups`,
          requestConfig,
        ),
        axiosInstance.get<PracticeApi[]>(
          `${FIREBASE_BASE_URL}/vetinsight/practices`,
          requestConfig,
        ),
      ]);

      const groups = groupsRes.data ?? [];
      const practices = practicesRes.data ?? [];
      const groupByKinshipId = new Map<string, PracticeGroupApi>();
      groups.forEach(g => groupByKinshipId.set(g.kinshipId, g));

      const practiceByKinshipId = new Map<string, PracticeApi>();
      practices.forEach(p => {
        if (!p?.kinshipId) return;
        if (!practiceByKinshipId.has(p.kinshipId))
          practiceByKinshipId.set(p.kinshipId, p);
      });

      const merged: PracticeRow[] = Array.from(
        practiceByKinshipId.values(),
      ).map(p => {
        const g = p.practiceGroupKinshipId
          ? groupByKinshipId.get(p.practiceGroupKinshipId)
          : undefined;
        const oohPhones = p.oohPhoneNumbers ?? [];
        return {
          id: p.kinshipId,

          url_code: safeStr(g?.urlCode ?? g?.code ?? ""),
          partner: safeStr(g?.partner ?? ""),
          practice_group_name: safeStr(g?.name ?? ""),

          practice_location_name: safeStr(p.name),
          practice_location_address: safeStr(p.address),
          practice_location_phone: safeStr(p.phone),
          practice_location_email: safeStr(p.email),
          practice_location_opening_hours: safeStr(p.hours),

          ooh_service: safeStr(p.oohService),
          ooh_phone: formatOohPhones(oohPhones),
          ooh_phone_numbers: oohPhones,

          vetstoria_clinic_hash: safeStr(p.vetstoriaClinicHash),
          vetstoria_location_id: safeStr(p.vetstoriaLocationId),

          multi_clinician_booking: safeBool(g?.multiClinicianBooking),
          vetstoria_api_version: safeStr(g?.vetstoriaApiVersion ?? ""),
          external_name: safeStr(p.externalName ?? g?.externalName ?? ""),
          quick_book_enabled: safeBool(g?.quickBookEnabled),
          crm_name: safeStr(g?.crmName ?? ""),
          sso_enabled: safeBool(p.ssoEnabled ?? g?.ssoEnabled),
          practice_group_id: safeStr(g?.practiceGroupId ?? ""),
          active:
            p.active !== null && p.active !== undefined
              ? safeBool(p.active)
              : safeBool(g?.active),
          auto_reply_enabled: safeBool(p.autoReplyEnabled),
          auto_reply_message: safeStr(p.autoReplyMessage),

          practice_kinship_id: safeStr(p.kinshipId),
          practice_group_kinship_id: safeStr(
            p.practiceGroupKinshipId ?? g?.kinshipId ?? "",
          ),
        };
      });

      merged.sort((a, b) => {
        const g = a.practice_group_name.localeCompare(b.practice_group_name);
        if (g !== 0) return g;
        return a.practice_location_name.localeCompare(b.practice_location_name);
      });

      setRows(merged);
    } catch (e: any) {
      // Our interceptor path
      if (e?.__kind === "ENTITLEMENT_DENIED") {
        setPermissionDenied(true);
        setError(
          e?.__message ||
            "You don’t have permission to access Practice Config Tool.",
        );
        setRows([]);
        return;
      }

      // Fallback: backend payload without interceptor
      const msg = String(e?.response?.data?.message || e?.message || "");
      if (
        e?.response?.status === 403 &&
        msg.toLowerCase().includes("entitlement denied for resource")
      ) {
        setPermissionDenied(true);
        setError(msg);
        setRows([]);
        return;
      }

      setError(msg || "Failed to load practice configuration.");
    } finally {
      setLoading(false);
    }
  }, [requestConfig]);

  React.useEffect(() => {
    void fetchAndMerge();
  }, [fetchAndMerge]);

  const filteredRows = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter(r => {
      const hay = [
        r.url_code,
        r.partner,
        r.practice_group_name,
        r.practice_location_name,
        r.practice_location_address,
        r.practice_location_phone,
        r.practice_location_email,
        r.vetstoria_location_id,
        r.vetstoria_clinic_hash,
        r.ooh_service,
        r.ooh_phone,
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, query]);

  const columns = React.useMemo<GridColDef<PracticeRow>[]>(
    () => [
      {
        field: "practice_group_name",
        headerName: "Practice Group",
        flex: 1.2,
        minWidth: 220,
      },
      { field: "url_code", headerName: "url_code", flex: 0.9, minWidth: 160 },
      {
        field: "practice_location_name",
        headerName: "Location",
        flex: 1.1,
        minWidth: 200,
      },
      {
        field: "practice_location_address",
        headerName: "Address",
        flex: 1.6,
        minWidth: 280,
      },
      {
        field: "practice_location_phone",
        headerName: "Phone",
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: "practice_location_email",
        headerName: "Email",
        flex: 1,
        minWidth: 220,
      },

      {
        field: "practice_location_opening_hours",
        headerName: "Opening Hours",
        flex: 1.2,
        minWidth: 220,
        renderCell: params => {
          const v = safeStr(params.value);
          if (!v) return "—";
          return (
            <Tooltip
              title={
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{v}</pre>
              }
            >
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                {v}
              </Box>
            </Tooltip>
          );
        },
      },

      {
        field: "ooh_service",
        headerName: "OOH Service",
        flex: 1,
        minWidth: 200,
      },

      {
        field: "ooh_phone_numbers",
        headerName: "OOH Numbers",
        flex: 1.2,
        minWidth: 260,
        sortable: false,
        valueGetter: params =>
          formatOohPhonesMultiline(
            (params.row as PracticeRow).ooh_phone_numbers,
          ),
        renderCell: params => {
          const v = (params.value as string) || "";
          if (!v) return "—";
          return (
            <Tooltip
              title={
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{v}</pre>
              }
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#111827",
                  whiteSpace: "pre-line",
                  lineHeight: 1.35,
                  py: 1,
                  width: "100%",
                }}
              >
                {v}
              </Typography>
            </Tooltip>
          );
        },
      },

      {
        field: "vetstoria_location_id",
        headerName: "Vetstoria Location ID",
        flex: 0.9,
        minWidth: 190,
      },
      {
        field: "vetstoria_clinic_hash",
        headerName: "Clinic Hash",
        flex: 1,
        minWidth: 190,
      },

      {
        field: "active",
        headerName: "Active",
        flex: 0.6,
        minWidth: 110,
        renderCell: p => (
          <Chip
            size="small"
            label={p.value ? "Active" : "Inactive"}
            sx={{
              fontWeight: 700,
              borderRadius: 999,
              bgcolor: p.value
                ? alpha("#10B981", 0.18)
                : alpha("#EF4444", 0.16),
              color: p.value ? "#10B981" : "#EF4444",
              border: `1px solid ${
                p.value ? alpha("#10B981", 0.35) : alpha("#EF4444", 0.35)
              }`,
            }}
          />
        ),
      },
    ],
    [],
  );

  return (
    <Box
      sx={{
        pb: 4,
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
        boxSizing: "border-box",
        px: { md: 0 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          borderRadius: 4,
          p: { xs: 2, sm: 2.5, md: 3 },
          mb: 2.5,
          background: "#FFFFFF",
          color: "#111827",
          border: "1px solid #E5E7EB",
          boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
        }}
      >
        <Typography variant="h5" fontWeight={900} sx={{ mb: 0.75 }}>
          Practice Configuration
        </Typography>

        <Typography variant="body2" sx={{ color: "#6B7280", mb: 1.5 }}>
          Manage, search, and update practice groups and locations with
          role-based permissions and full audit history.
        </Typography>

        <TextField
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search group, location, url_code, address, vetstoria id..."
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: alpha("#111827", 0.55) }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 680 },
            "& .MuiOutlinedInput-root": {
              color: "#000000",
              background: "#FFFFFF",
            },
            "& input::placeholder": {
              color: alpha("#111827", 0.45),
              opacity: 1,
            },
          }}
        />
      </Paper>

      {permissionDenied ? (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 4,
            border: "1px solid #FCA5A5",
            background: alpha("#EF4444", 0.06),
            mb: 2,
          }}
        >
          <Typography fontWeight={900} sx={{ color: "#991B1B", mb: 0.5 }}>
            Access denied
          </Typography>
          <Typography variant="body2" sx={{ color: "#7F1D1D" }}>
            {error || "You don’t have permission to access this page."}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: "#7F1D1D", fontWeight: 700 }}
            >
              Ask your admin to grant: <code>create_practice_tool:get</code>
            </Typography>
          </Stack>
        </Paper>
      ) : error ? (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 4,
            border: "1px solid #E5E7EB",
            background: "#FFF",
            mb: 2,
          }}
        >
          <Typography fontWeight={900} sx={{ mb: 0.5 }}>
            Something went wrong
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            {error}
          </Typography>
        </Paper>
      ) : null}

      {isMobile ? (
        <Stack spacing={1.25} sx={{ maxWidth: "100%", overflowX: "hidden" }}>
          {loading ? (
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 3, border: "1px solid #E5E7EB" }}
            >
              <Typography fontWeight={800}>Loading…</Typography>
            </Paper>
          ) : null}

          {!loading && filteredRows.length === 0 ? (
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 3, border: "1px solid #E5E7EB" }}
            >
              <Typography fontWeight={900}>No results</Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                Try a different search.
              </Typography>
            </Paper>
          ) : null}

          {filteredRows.map(r => (
            <Card
              key={r.id}
              variant="outlined"
              onClick={() =>
                navigate(`/practice-config-tool/edit/${r.practice_kinship_id}`)
              }
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                borderRadius: 3,
                borderColor: "#E5E7EB",
                cursor: "pointer",
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  "&:last-child": { pb: 2 },
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "flex-start" }}
                  justifyContent="space-between"
                  sx={{ width: "100%", minWidth: 0 }}
                >
                  <Box sx={{ minWidth: 0, width: "100%" }}>
                    <Typography
                      fontWeight={900}
                      sx={{
                        lineHeight: 1.2,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {r.practice_location_name || "—"}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6B7280",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {r.practice_group_name || "—"}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={r.active ? "Active" : "Inactive"}
                    sx={{
                      fontWeight: 800,
                      borderRadius: 999,
                      bgcolor: r.active
                        ? alpha("#10B981", 0.18)
                        : alpha("#EF4444", 0.16),
                      color: r.active ? "#10B981" : "#EF4444",
                      border: `1px solid ${
                        r.active
                          ? alpha("#10B981", 0.35)
                          : alpha("#EF4444", 0.35)
                      }`,
                      flexShrink: 0,
                      alignSelf: { xs: "flex-start", sm: "flex-start" },
                    }}
                  />
                </Stack>

                {r.practice_location_address ? (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: "#111827",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    <strong>Address:</strong> {r.practice_location_address}
                  </Typography>
                ) : null}

                <Stack
                  spacing={0.5}
                  sx={{ mt: 1, maxWidth: "100%", minWidth: 0 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                  >
                    <strong>Phone:</strong> {r.practice_location_phone || "—"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                  >
                    <strong>Email:</strong> {r.practice_location_email || "—"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                  >
                    <strong>Vetstoria:</strong> {r.vetstoria_location_id || "—"}
                  </Typography>
                </Stack>

                {r.ooh_phone_numbers?.length ? (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    <strong>OOH:</strong>{" "}
                    {formatOohPhones(r.ooh_phone_numbers) || "—"}
                  </Typography>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            background: "#FFFFFF",
            border: "1px solid #E5E7EB",
            boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
          }}
        >
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              loading={loading}
              disableSelectionOnClick
              onRowClick={params =>
                navigate(
                  `/practice-config-tool/edit/${params.row.practice_kinship_id}`,
                )
              }
              autoHeight
              getRowHeight={() => "auto"}
              sx={{
                border: "none",
                backgroundColor: "#FFFFFF",
                color: "#111827",
                "& .MuiDataGrid-row": { cursor: "pointer" },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#F9FAFB",
                  color: "#111827",
                  borderBottom: "1px solid #E5E7EB",
                  fontWeight: 800,
                },
                "& .MuiDataGrid-cell": {
                  color: "#111827",
                  borderBottom: "1px solid #F1F5F9",
                  alignItems: "flex-start",
                  py: 1.25,
                  whiteSpace: "normal",
                  lineHeight: 1.35,
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#FCFCFD",
                },
                "& .MuiDataGrid-row:hover": { backgroundColor: "#F3F4F6" },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid #E5E7EB",
                  backgroundColor: "#F9FAFB",
                  color: "#111827",
                },
              }}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
}
